/**
 * App state store + persistence + pure gating/conversion helpers.
 *
 * This module is intentionally free of any top-level DOM access so it imports
 * cleanly under node (vitest). The one browser-only routine, decodeImageFile,
 * guards on `document` and is never exercised by the node test suite.
 */
import type {
  CutParams,
  PhotoFillResult,
  PhotoFillSettings,
  PhotoPaletteEntry,
  PhotoResult,
  PixelResult,
  PixelSettings,
  PhotoSettings,
  Pixel,
  RawImage,
  SwatchEntry,
  SwatchLibrary,
  TestGridSettings,
  UnitSystem,
  VectorDocument,
} from "../core/types";
import {
  DEFAULT_PHOTO_FILL,
  DEFAULT_PHOTO_SETTINGS,
  DEFAULT_PIXEL_SETTINGS,
  DEFAULT_TEST_GRID,
  MM_PER_INCH,
} from "../core/types";

// ---------------------------------------------------------------------------
// State shape.
// ---------------------------------------------------------------------------

export type TabId = "vector" | "photo" | "tools";

export interface AppState {
  activeTab: TabId;

  // Vector workflow.
  doc: VectorDocument | null;
  docFileName: string | null;
  selectedLayers: Set<number>;
  pixel: PixelSettings;
  replaceOriginal: boolean;
  livePreviewCap: number;
  photoFill: PhotoFillSettings;
  photoFillImage: RawImage | null;
  vectorResult: PixelResult | null;
  vectorFillResult: PhotoFillResult | null;
  vectorTruncated: boolean;
  /** Signature of the last FULL, non-truncated vector generation (export gate). */
  vectorFullSignature: string | null;

  // Photo workflow.
  photo: PhotoSettings;
  photoImage: RawImage | null;
  photoResult: PhotoResult | null;
  photoResultSignature: string | null;

  // Shared.
  cutParams: Record<number, CutParams>;
  testGrid: TestGridSettings;
  reportMachine: string;
  reportMaterial: string;

  // Units / preferences.
  unit: UnitSystem;
  firstRunDone: boolean;
  swatchLibraries: SwatchLibrary[];
  activeSwatchLibraryId: string | null;

  // View / status.
  zoom: number;
  status: string;
  busy: boolean;
}

export const STORAGE_KEY = "mopa-pixelator-v1";
/** Bumped to 2 for the v3 shape (unit, firstRunDone, swatch libraries; contour→clip migration). */
export const PERSIST_VERSION = 2;
export const DEFAULT_LIVE_PREVIEW_CAP = 30000;

// ---------------------------------------------------------------------------
// Pub/sub store.
// ---------------------------------------------------------------------------

export type Updater = (state: AppState) => Partial<AppState>;
type Listener = (state: AppState) => void;

export class Store {
  private state: AppState;
  private listeners = new Set<Listener>();

  constructor(initial: AppState) {
    this.state = initial;
  }

  get(): AppState {
    return this.state;
  }

  /** Shallow-merge a patch (or an updater's patch) and notify subscribers. */
  set(patch: Partial<AppState> | Updater): void {
    const delta = typeof patch === "function" ? patch(this.state) : patch;
    this.state = { ...this.state, ...delta };
    for (const l of [...this.listeners]) l(this.state);
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
}

// ---------------------------------------------------------------------------
// Defaults + validation.
// ---------------------------------------------------------------------------

function num(v: unknown, def: number): number {
  const n = typeof v === "number" ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : def;
}

function bool(v: unknown, def: boolean): boolean {
  return typeof v === "boolean" ? v : def;
}

function pick<T extends string>(v: unknown, allowed: readonly T[], def: T): T {
  return allowed.includes(v as T) ? (v as T) : def;
}

function clonePixel(p: PixelSettings): PixelSettings {
  return { ...p };
}

export function defaultAppState(): AppState {
  return {
    activeTab: "vector",
    doc: null,
    docFileName: null,
    selectedLayers: new Set<number>(),
    pixel: clonePixel(DEFAULT_PIXEL_SETTINGS),
    replaceOriginal: false,
    livePreviewCap: DEFAULT_LIVE_PREVIEW_CAP,
    photoFill: { ...DEFAULT_PHOTO_FILL },
    photoFillImage: null,
    vectorResult: null,
    vectorFillResult: null,
    vectorTruncated: false,
    vectorFullSignature: null,
    photo: { ...DEFAULT_PHOTO_SETTINGS },
    photoImage: null,
    photoResult: null,
    photoResultSignature: null,
    cutParams: {},
    testGrid: JSON.parse(JSON.stringify(DEFAULT_TEST_GRID)) as TestGridSettings,
    reportMachine: "MOPA fiber laser",
    reportMaterial: "Stainless 304/316",
    unit: "mm",
    firstRunDone: false,
    swatchLibraries: [],
    activeSwatchLibraryId: null,
    zoom: 1,
    status: "Open a LightBurn or SVG file, or drop a photo, to begin.",
    busy: false,
  };
}

// ---------------------------------------------------------------------------
// Persistence (tolerant of malformed / partial / older payloads).
// ---------------------------------------------------------------------------

export interface PersistedState {
  version: number;
  activeTab: TabId;
  pixel: PixelSettings;
  photo: PhotoSettings;
  photoFill: PhotoFillSettings;
  replaceOriginal: boolean;
  livePreviewCap: number;
  cutParams: Record<number, CutParams>;
  testGrid: TestGridSettings;
  unit: UnitSystem;
  firstRunDone: boolean;
  swatchLibraries: SwatchLibrary[];
  activeSwatchLibraryId: string | null;
}

const SHAPES = ["circle", "square", "diamond", "hexagon", "octagon", "triangle", "star", "heart", "cross"] as const;
const MODES = ["auto", "fill", "trace"] as const;
const ORIGINS = ["project", "bbox"] as const;
const EDGE_MODES = ["none", "clip"] as const;
const UNITS = ["mm", "inch"] as const;
const PALETTE_MODES = ["adaptive", "lightburn"] as const;

/**
 * Migrate + validate a persisted edge mode. The retired "contour" variant maps
 * forward to "clip" so old saved sessions still open with an equivalent edge
 * treatment; any unrecognized value falls back to the default.
 */
function normEdgeMode(v: unknown, def: PixelSettings["edgeMode"]): PixelSettings["edgeMode"] {
  if (v === "contour") return "clip";
  return pick(v, EDGE_MODES, def);
}
const SORT_MODES = ["dark-to-light", "light-to-dark", "most-used", "hue"] as const;
const FITS = ["cover", "contain", "stretch"] as const;
const SWEEP_PARAMS = ["power", "speed", "frequencyKhz", "qPulseNs", "intervalMm"] as const;
const TABS = ["vector", "photo", "tools"] as const;

function normPixel(raw: unknown): PixelSettings {
  const o = (raw ?? {}) as Record<string, unknown>;
  const d = DEFAULT_PIXEL_SETTINGS;
  return {
    pixelSize: Math.max(0.0001, num(o.pixelSize, d.pixelSize)),
    spacingX: Math.max(0.0001, num(o.spacingX, d.spacingX)),
    spacingY: Math.max(0.0001, num(o.spacingY, d.spacingY)),
    shape: pick(o.shape, SHAPES, d.shape),
    mode: pick(o.mode, MODES, d.mode),
    origin: pick(o.origin, ORIGINS, d.origin),
    offsetX: num(o.offsetX, d.offsetX),
    offsetY: num(o.offsetY, d.offsetY),
    stagger: bool(o.stagger, d.stagger),
    hexPacking: bool(o.hexPacking, d.hexPacking),
    aspect: Math.max(0.25, Math.min(4, num(o.aspect, d.aspect))),
    flipOffset: bool(o.flipOffset, d.flipOffset),
    curveSteps: Math.max(3, Math.min(96, Math.round(num(o.curveSteps, d.curveSteps)))),
    edgeMode: normEdgeMode(o.edgeMode, d.edgeMode),
    maxPixels: Math.max(1, Math.round(num(o.maxPixels, d.maxPixels))),
  };
}

function normPhoto(raw: unknown): PhotoSettings {
  const o = (raw ?? {}) as Record<string, unknown>;
  const d = DEFAULT_PHOTO_SETTINGS;
  return {
    maxLayers: Math.max(1, Math.min(30, Math.round(num(o.maxLayers, d.maxLayers)))),
    outputWidthMm: Math.max(1, num(o.outputWidthMm, d.outputWidthMm)),
    pixelSize: Math.max(0.0001, num(o.pixelSize, d.pixelSize)),
    spacingX: Math.max(0.0001, num(o.spacingX, d.spacingX)),
    spacingY: Math.max(0.0001, num(o.spacingY, d.spacingY)),
    shape: pick(o.shape, SHAPES, d.shape),
    paletteMode: pick(o.paletteMode, PALETTE_MODES, d.paletteMode),
    sortMode: pick(o.sortMode, SORT_MODES, d.sortMode),
    dither: bool(o.dither, d.dither),
    ignoreTransparent: bool(o.ignoreTransparent, d.ignoreTransparent),
    ignoreNearWhite: bool(o.ignoreNearWhite, d.ignoreNearWhite),
    startLayer: Math.max(0, Math.min(29, Math.round(num(o.startLayer, d.startLayer)))),
    maxPixels: Math.max(1, Math.round(num(o.maxPixels, d.maxPixels))),
    optimizeSquareRuns: bool(o.optimizeSquareRuns, d.optimizeSquareRuns),
  };
}

function normPhotoFill(raw: unknown): PhotoFillSettings {
  const o = (raw ?? {}) as Record<string, unknown>;
  const d = DEFAULT_PHOTO_FILL;
  return {
    enabled: bool(o.enabled, d.enabled),
    fit: pick(o.fit, FITS, d.fit),
    maxLayers: Math.max(1, Math.min(30, Math.round(num(o.maxLayers, d.maxLayers)))),
    paletteMode: pick(o.paletteMode, PALETTE_MODES, d.paletteMode),
    sortMode: pick(o.sortMode, SORT_MODES, d.sortMode),
    dither: bool(o.dither, d.dither),
    startLayer: Math.max(0, Math.min(29, Math.round(num(o.startLayer, d.startLayer)))),
  };
}

export function normCutParams(raw: unknown): CutParams {
  const o = (raw ?? {}) as Record<string, unknown>;
  return {
    power: Math.max(0, Math.min(100, num(o.power, 20))),
    speed: Math.max(1, num(o.speed, 800)),
    frequencyKhz: Math.max(1, Math.min(4000, num(o.frequencyKhz, 500))),
    qPulseNs: Math.max(1, num(o.qPulseNs, 6)),
    intervalMm: Math.max(0.001, num(o.intervalMm, 0.01)),
    passes: Math.max(1, Math.round(num(o.passes, 1))),
    hatchAngleDeg: ((num(o.hatchAngleDeg, 0) % 360) + 360) % 360,
    crossHatch: bool(o.crossHatch, false),
    bidir: bool(o.bidir, true),
    fillAllAtOnce: bool(o.fillAllAtOnce, false),
  };
}

function str(v: unknown, def: string): string {
  return typeof v === "string" ? v : def;
}

function normHexColor(v: unknown, def: string): string {
  if (typeof v === "string") {
    const m = v.trim().toUpperCase();
    if (/^#[0-9A-F]{6}$/.test(m)) return m;
  }
  return def;
}

function normSwatchEntry(raw: unknown): SwatchEntry {
  const o = (raw ?? {}) as Record<string, unknown>;
  return {
    id: str(o.id, "sw-" + Math.random().toString(36).slice(2, 10)),
    measuredColor: normHexColor(o.measuredColor, "#808080"),
    label: str(o.label, ""),
    params: normCutParams(o.params),
    focusOffsetMm: num(o.focusOffsetMm, 0),
  };
}

function normSwatchLibrary(raw: unknown): SwatchLibrary {
  const o = (raw ?? {}) as Record<string, unknown>;
  const swatches = Array.isArray(o.swatches) ? o.swatches.map(normSwatchEntry) : [];
  return {
    id: str(o.id, "lib-" + Math.random().toString(36).slice(2, 10)),
    name: str(o.name, "Untitled library"),
    machine: str(o.machine, ""),
    material: str(o.material, ""),
    finish: str(o.finish, ""),
    swatches,
  };
}

function normSwatchLibraries(raw: unknown): SwatchLibrary[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(normSwatchLibrary);
}

function normCutParamsMap(raw: unknown): Record<number, CutParams> {
  const out: Record<number, CutParams> = {};
  if (raw && typeof raw === "object") {
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
      const idx = Number(k);
      if (Number.isFinite(idx)) out[idx] = normCutParams(v);
    }
  }
  return out;
}

function normSweepValues(raw: unknown, def: number[]): number[] {
  if (!Array.isArray(raw)) return [...def];
  const vals = raw.map((v) => num(v, NaN)).filter((v) => Number.isFinite(v));
  return vals.length ? vals.slice(0, 8) : [...def];
}

function normTestGrid(raw: unknown): TestGridSettings {
  const o = (raw ?? {}) as Record<string, unknown>;
  const d = DEFAULT_TEST_GRID;
  return {
    rowParam: pick(o.rowParam, SWEEP_PARAMS, d.rowParam),
    rowValues: normSweepValues(o.rowValues, d.rowValues),
    colParam: pick(o.colParam, SWEEP_PARAMS, d.colParam),
    colValues: normSweepValues(o.colValues, d.colValues),
    base: normCutParams(o.base),
    swatchSizeMm: Math.max(1, num(o.swatchSizeMm, d.swatchSizeMm)),
    gapMm: Math.max(1, num(o.gapMm, d.gapMm)),
    labelCells: bool(o.labelCells, d.labelCells),
  };
}

export function extractPersisted(state: AppState): PersistedState {
  return {
    version: PERSIST_VERSION,
    activeTab: state.activeTab,
    pixel: state.pixel,
    photo: state.photo,
    photoFill: state.photoFill,
    replaceOriginal: state.replaceOriginal,
    livePreviewCap: state.livePreviewCap,
    cutParams: state.cutParams,
    testGrid: state.testGrid,
    unit: state.unit,
    firstRunDone: state.firstRunDone,
    swatchLibraries: state.swatchLibraries,
    activeSwatchLibraryId: state.activeSwatchLibraryId,
  };
}

/** Parse+normalize a raw persisted payload into a fully-valid PersistedState. */
export function normalizePersisted(raw: unknown): PersistedState {
  const o = (raw ?? {}) as Record<string, unknown>;
  return {
    version: PERSIST_VERSION,
    activeTab: pick(o.activeTab, TABS, "vector"),
    pixel: normPixel(o.pixel),
    photo: normPhoto(o.photo),
    photoFill: normPhotoFill(o.photoFill),
    replaceOriginal: bool(o.replaceOriginal, false),
    livePreviewCap: Math.max(100, Math.round(num(o.livePreviewCap, DEFAULT_LIVE_PREVIEW_CAP))),
    cutParams: normCutParamsMap(o.cutParams),
    testGrid: normTestGrid(o.testGrid),
    unit: pick(o.unit, UNITS, "mm"),
    firstRunDone: bool(o.firstRunDone, false),
    swatchLibraries: normSwatchLibraries(o.swatchLibraries),
    activeSwatchLibraryId: typeof o.activeSwatchLibraryId === "string" ? o.activeSwatchLibraryId : null,
  };
}

export function loadPersisted(): PersistedState | null {
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return normalizePersisted(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function savePersisted(state: AppState): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(extractPersisted(state)));
  } catch {
    /* storage full or blocked — persistence is best-effort */
  }
}

export function applyPersisted(base: AppState, p: PersistedState): AppState {
  return {
    ...base,
    activeTab: p.activeTab,
    pixel: p.pixel,
    photo: p.photo,
    photoFill: p.photoFill,
    replaceOriginal: p.replaceOriginal,
    livePreviewCap: p.livePreviewCap,
    cutParams: p.cutParams,
    testGrid: p.testGrid,
    unit: p.unit,
    firstRunDone: p.firstRunDone,
    swatchLibraries: p.swatchLibraries,
    activeSwatchLibraryId: p.activeSwatchLibraryId,
  };
}

export function createInitialState(): AppState {
  const base = defaultAppState();
  const persisted = loadPersisted();
  return persisted ? applyPersisted(base, persisted) : base;
}

// ---------------------------------------------------------------------------
// Settings-signature gating.
//
// A signature captures every setting that affects generated geometry / color
// assignment. Exports are gated on a FULL, non-truncated result whose signature
// still matches the current settings; a stale or truncated result can never be
// exported. Fields that do not change geometry (replaceOriginal, the live
// preview cap) are deliberately excluded.
// ---------------------------------------------------------------------------

function r7(n: number): number {
  return Math.round(num(n, 0) * 1e7) / 1e7;
}

export interface VectorSignatureInput {
  fileName: string | null;
  layers: Iterable<number>;
  pixel: PixelSettings;
  photoFill: PhotoFillSettings;
  photoFillImageName: string | null;
}

export function vectorSignature(input: VectorSignatureInput): string {
  const p = input.pixel;
  const layers = [...input.layers].sort((a, b) => a - b);
  const parts: (string | number)[] = [
    input.fileName ?? "",
    layers.join(","),
    r7(p.pixelSize),
    r7(p.spacingX),
    r7(p.spacingY),
    p.shape,
    p.mode,
    p.origin,
    r7(p.offsetX),
    r7(p.offsetY),
    p.stagger ? 1 : 0,
    p.curveSteps,
    p.edgeMode,
    p.maxPixels,
  ];
  const f = input.photoFill;
  if (f.enabled && input.photoFillImageName) {
    parts.push(
      "fill",
      input.photoFillImageName,
      f.fit,
      f.maxLayers,
      f.paletteMode,
      f.sortMode,
      f.dither ? 1 : 0,
      f.startLayer,
    );
  }
  return parts.join("|");
}

export function photoSignature(imageName: string | null, s: PhotoSettings): string {
  return [
    imageName ?? "",
    Math.round(s.maxLayers),
    r7(s.outputWidthMm),
    r7(s.pixelSize),
    r7(s.spacingX),
    r7(s.spacingY),
    s.shape,
    s.paletteMode,
    s.sortMode,
    s.dither ? 1 : 0,
    s.ignoreTransparent ? 1 : 0,
    s.ignoreNearWhite ? 1 : 0,
    s.startLayer,
    s.maxPixels,
    s.optimizeSquareRuns ? 1 : 0,
  ].join("|");
}

/** Exports are allowed only against a cached full signature matching the current one. */
export function exportReady(current: string, cachedFull: string | null): boolean {
  return cachedFull !== null && cachedFull === current;
}

// ---------------------------------------------------------------------------
// Pure conversions between engine outputs and export inputs.
// ---------------------------------------------------------------------------

export function photoPaletteLayerColors(palette: readonly PhotoPaletteEntry[]): Record<number, string> {
  const out: Record<number, string> = {};
  for (const e of palette) out[e.layerIndex] = e.svgColor;
  return out;
}

/** Explode a grid PhotoResult into full grid-center dots for canvas + lbrn export. */
export function photoResultToPixels(result: PhotoResult): Pixel[] {
  const { cols, rows, originX, originY, spacingX, spacingY, grid, palette } = result;
  const layerByOrdinal = palette.map((p) => p.layerIndex);
  const out: Pixel[] = [];
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const v = grid[row * cols + col];
      if (v === 0) continue;
      const ord = v - 1;
      if (ord < 0 || ord >= layerByOrdinal.length) continue;
      out.push({
        x: originX + col * spacingX,
        y: originY + row * spacingY,
        layer: layerByOrdinal[ord],
        sizeFactor: 1,
      });
    }
  }
  return out;
}

function pixelsBBox(pixels: readonly Pixel[]): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of pixels) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  if (!Number.isFinite(minX)) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  return { minX, minY, maxX, maxY };
}

/**
 * Synthesize a grid PhotoResult from a photo-fill result so the shared palette
 * CSV / report exporters (which consume a PhotoResult) work for vector-fill.
 * The palette carries the authoritative per-layer counts. The exporters never
 * read the grid on this path, so it stays EMPTY — a real cols*rows allocation
 * could run to hundreds of MB for large sparse artwork at fine spacing.
 */
export function buildPhotoResultFromFill(
  fill: PhotoFillResult,
  pixel: PixelSettings,
  photoFill: PhotoFillSettings,
  source: { name: string; width: number; height: number },
): PhotoResult {
  const bbox = pixelsBBox(fill.pixels);
  const spacingX = Math.max(0.0001, pixel.spacingX);
  const spacingY = Math.max(0.0001, pixel.spacingY);
  const cols = Math.max(1, Math.round((bbox.maxX - bbox.minX) / spacingX) + 1);
  const rows = Math.max(1, Math.round((bbox.maxY - bbox.minY) / spacingY) + 1);
  const grid = new Uint8Array(0);
  return {
    cols,
    rows,
    originX: bbox.minX,
    originY: bbox.minY,
    spacingX,
    spacingY,
    pixelSize: pixel.pixelSize,
    shape: pixel.shape,
    grid,
    palette: fill.palette,
    paletteMode: photoFill.paletteMode,
    sourceName: source.name,
    sourceWidth: source.width,
    sourceHeight: source.height,
    widthMm: Math.max(0.001, bbox.maxX - bbox.minX),
    heightMm: Math.max(0.001, bbox.maxY - bbox.minY),
    visibleCount: fill.pixels.length,
    notes: ["Geometry from vector outline; colors sampled from image via photo fill."],
  };
}

// ---------------------------------------------------------------------------
// Units + locale-tolerant number I/O (pure; node-testable).
//
// The engine and all geometry are ALWAYS millimeters internally. These helpers
// convert only at the UI boundary: field display/parse and ruler readouts.
// parseLocaleNumber accepts comma-or-period decimals ("0,25" -> 0.25) and a
// trailing unit token, so a user in a comma locale (half the world) can type
// naturally. mmToUnit / unitToMm bridge the display value <-> stored mm value.
// ---------------------------------------------------------------------------

export const UNIT_LABEL: Record<UnitSystem, string> = { mm: "mm", inch: "in" };

/** Convert a stored millimeter value to the active display unit. */
export function mmToUnit(mm: number, unit: UnitSystem): number {
  return unit === "inch" ? mm / MM_PER_INCH : mm;
}

/** Convert a display-unit value back to millimeters for storage/engine. */
export function unitToMm(value: number, unit: UnitSystem): number {
  return unit === "inch" ? value * MM_PER_INCH : value;
}

/**
 * Parse a numeric field value tolerantly: accepts comma OR period decimal
 * separators, surrounding whitespace, a leading "+", and a trailing unit token
 * ("0,25 mm", "1.5in"). Returns null when there is no parseable number, so the
 * caller can flag the field instead of silently coercing to 0/NaN.
 */
export function parseLocaleNumber(raw: string): number | null {
  if (raw == null) return null;
  let s = String(raw).trim();
  if (!s) return null;
  // Strip a trailing unit token (letters/°/%) so "1.5 mm" parses.
  s = s.replace(/[a-zA-Z°%\s]+$/, "").trim();
  if (!s) return null;
  // Normalize decimal comma to period. If BOTH separators appear, treat the
  // LAST one as the decimal point and drop the others (thousands separators).
  const lastComma = s.lastIndexOf(",");
  const lastDot = s.lastIndexOf(".");
  if (lastComma >= 0 && lastDot >= 0) {
    const decimalPos = Math.max(lastComma, lastDot);
    const intPart = s.slice(0, decimalPos).replace(/[.,]/g, "");
    const fracPart = s.slice(decimalPos + 1);
    s = intPart + "." + fracPart;
  } else if (lastComma >= 0) {
    s = s.replace(",", ".");
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** Trim trailing zeros from a fixed-decimal string ("1.500" -> "1.5", "2.00" -> "2"). */
function trimZeros(s: string): string {
  return s.includes(".") ? s.replace(/\.?0+$/, "") : s;
}

/**
 * Format a stored millimeter value for display in the active unit. Inches get
 * more decimals (0.001 mm ≈ 0.00004 in) so fine values do not collapse to 0.
 */
export function formatDim(mm: number, unit: UnitSystem, decimals?: number): string {
  const v = mmToUnit(mm, unit);
  const dp = decimals ?? (unit === "inch" ? 4 : 3);
  return trimZeros(v.toFixed(dp));
}

// ---------------------------------------------------------------------------
// Browser-only helpers (guarded; never hit in node tests).
// ---------------------------------------------------------------------------

export async function fileToBytes(file: Blob): Promise<Uint8Array> {
  const buf = await file.arrayBuffer();
  return new Uint8Array(buf);
}

/** Decode text bytes honoring a UTF-16 LE/BE BOM; default UTF-8 (BOM stripped). */
export function decodeTextSniffed(bytes: Uint8Array): string {
  if (bytes.length >= 2) {
    if (bytes[0] === 0xff && bytes[1] === 0xfe) return new TextDecoder("utf-16le").decode(bytes);
    if (bytes[0] === 0xfe && bytes[1] === 0xff) return new TextDecoder("utf-16be").decode(bytes);
  }
  return new TextDecoder("utf-8").decode(bytes);
}

function loadImageElement(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Image could not be decoded"));
    img.src = url;
  });
}

export interface DecodeImageOptions {
  /** SVG inputs are rasterized at this multiple of their intrinsic pixel size. */
  svgScale?: number;
  maxDim?: number;
}

/**
 * Decode an image File/Blob to RawImage RGBA. SVG inputs have no intrinsic
 * raster, so they are drawn to a canvas at `svgScale`x their declared size
 * (default 4x) — this is the documented rasterization of SVG photo input.
 */
export async function decodeImageFile(
  file: Blob,
  name: string,
  opts: DecodeImageOptions = {},
): Promise<RawImage> {
  if (typeof document === "undefined") {
    throw new Error("Image decoding requires a browser environment");
  }
  const isSvg = file.type === "image/svg+xml" || /\.svg$/i.test(name);
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImageElement(url);
    let w = img.naturalWidth || img.width || 0;
    let h = img.naturalHeight || img.height || 0;
    if (isSvg) {
      const scale = opts.svgScale ?? 4;
      w = Math.round((w || 512) * scale);
      h = Math.round((h || 512) * scale);
    }
    w = Math.max(1, w);
    h = Math.max(1, h);
    const maxDim = opts.maxDim ?? 4096;
    if (w > maxDim || h > maxDim) {
      const s = maxDim / Math.max(w, h);
      w = Math.max(1, Math.round(w * s));
      h = Math.max(1, Math.round(h * s));
    }
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) throw new Error("Canvas 2D context unavailable");
    ctx.drawImage(img, 0, 0, w, h);
    const imageData = ctx.getImageData(0, 0, w, h);
    return { width: w, height: h, data: new Uint8ClampedArray(imageData.data), name };
  } finally {
    URL.revokeObjectURL(url);
  }
}
